import React, { useState, useEffect, useCallback } from 'react';
import { noticeAPI } from '../../api/client';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Bell, Plus, Edit, Trash2, Eye, EyeOff, Image, Type, 
  Calendar, Clock, Users, BarChart3, X, Upload, AlertCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Badge } from '../ui/badge';

const NoticeManagement = () => {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [noticeStats, setNoticeStats] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content_type: 'text',
    content: '',
    start_datetime: '',
    end_datetime: '',
    show_delay_seconds: 3,
    max_total_views: 1,
    max_views_per_day: 1,
    show_again_after_hours: '',
    link_url: '',
    link_text: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchNotices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await noticeAPI.list();
      setNotices(response.data);
    } catch (error) {
      toast.error('Failed to load notices');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotices();
  }, [fetchNotices]);

  const resetForm = () => {
    setFormData({
      title: '',
      content_type: 'text',
      content: '',
      start_datetime: '',
      end_datetime: '',
      show_delay_seconds: 3,
      max_total_views: 1,
      max_views_per_day: 1,
      show_again_after_hours: '',
      link_url: '',
      link_text: ''
    });
    setImageFile(null);
    setImagePreview(null);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreateNotice = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Prepare show_again_after_hours (convert to number or null)
      const showAgainHours = formData.show_again_after_hours ? parseFloat(formData.show_again_after_hours) : null;
      
      if (formData.content_type === 'image' && imageFile) {
        // Create with image using FormData
        const params = new URLSearchParams({
          title: formData.title,
          start_datetime: new Date(formData.start_datetime).toISOString(),
          end_datetime: new Date(formData.end_datetime).toISOString(),
          show_delay_seconds: formData.show_delay_seconds,
          max_total_views: formData.max_total_views,
          max_views_per_day: formData.max_views_per_day
        });
        if (showAgainHours !== null) {
          params.append('show_again_after_hours', showAgainHours);
        }
        if (formData.link_url) {
          params.append('link_url', formData.link_url);
        }
        if (formData.link_text) {
          params.append('link_text', formData.link_text);
        }
        
        const formDataObj = new FormData();
        formDataObj.append('image', imageFile);
        
        await noticeAPI.createWithImage(formDataObj, params.toString());
      } else {
        // Create text notice
        const noticeData = {
          ...formData,
          start_datetime: new Date(formData.start_datetime).toISOString(),
          end_datetime: new Date(formData.end_datetime).toISOString(),
          show_again_after_hours: showAgainHours,
          link_url: formData.link_url || null,
          link_text: formData.link_text || null
        };
        await noticeAPI.create(noticeData);
      }

      toast.success('Notice created successfully!');
      setShowCreateModal(false);
      resetForm();
      fetchNotices();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else if (Array.isArray(errorMsg)) {
        toast.error(errorMsg[0]?.msg || 'Validation error');
      } else {
        toast.error('Failed to create notice');
      }
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateNotice = async (e) => {
    e.preventDefault();
    if (!selectedNotice) return;
    setSubmitting(true);

    try {
      const updateData = {
        title: formData.title,
        content: formData.content,
        start_datetime: new Date(formData.start_datetime).toISOString(),
        end_datetime: new Date(formData.end_datetime).toISOString(),
        show_delay_seconds: parseInt(formData.show_delay_seconds),
        max_total_views: parseInt(formData.max_total_views),
        max_views_per_day: parseInt(formData.max_views_per_day),
        show_again_after_hours: formData.show_again_after_hours ? parseFloat(formData.show_again_after_hours) : null,
        link_url: formData.link_url || null,
        link_text: formData.link_text || null
      };

      await noticeAPI.update(selectedNotice.id, updateData);
      toast.success('Notice updated successfully!');
      setShowEditModal(false);
      setSelectedNotice(null);
      resetForm();
      fetchNotices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update notice');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTogglePublish = async (notice) => {
    try {
      const response = await noticeAPI.togglePublish(notice.id);
      toast.success(response.data.message);
      fetchNotices();
    } catch (error) {
      toast.error('Failed to update notice status');
      console.error(error);
    }
  };

  const handleDeleteNotice = async (notice) => {
    if (!window.confirm(`Are you sure you want to delete "${notice.title}"?`)) return;

    try {
      await noticeAPI.delete(notice.id);
      toast.success('Notice deleted successfully!');
      fetchNotices();
    } catch (error) {
      toast.error('Failed to delete notice');
      console.error(error);
    }
  };

  const handleViewStats = async (notice) => {
    try {
      const response = await noticeAPI.getStats(notice.id);
      setNoticeStats(response.data);
      setSelectedNotice(notice);
      setShowStatsModal(true);
    } catch (error) {
      toast.error('Failed to load statistics');
      console.error(error);
    }
  };

  const openEditModal = (notice) => {
    setSelectedNotice(notice);
    setFormData({
      title: notice.title,
      content_type: notice.content_type,
      content: notice.content,
      start_datetime: notice.start_datetime.slice(0, 16),
      end_datetime: notice.end_datetime.slice(0, 16),
      show_delay_seconds: notice.show_delay_seconds,
      max_total_views: notice.max_total_views,
      max_views_per_day: notice.max_views_per_day,
      show_again_after_hours: notice.show_again_after_hours || '',
      link_url: notice.link_url || '',
      link_text: notice.link_text || ''
    });
    if (notice.content_type === 'image') {
      setImagePreview(notice.content);
    }
    setShowEditModal(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800',
      scheduled: 'bg-blue-100 text-blue-800',
      active: 'bg-green-100 text-green-800',
      expired: 'bg-red-100 text-red-800'
    };
    return (
      <Badge className={styles[status] || styles.draft}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const formatDateTime = (dateStr) => {
    try {
      return format(new Date(dateStr), 'dd MMM yyyy, hh:mm a');
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notice Management
          </h2>
          <p className="text-sm text-gray-500">Create and manage student notices</p>
        </div>
        <Button onClick={() => { resetForm(); setShowCreateModal(true); }}>
          <Plus className="h-4 w-4 mr-2" />
          Create Notice
        </Button>
      </div>

      {/* Notice List */}
      {loading ? (
        <div className="text-center py-8">Loading notices...</div>
      ) : notices.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <Bell className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">No notices created yet</p>
            <Button className="mt-4" onClick={() => setShowCreateModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Your First Notice
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {notices.map((notice) => (
            <Card key={notice.id} className="overflow-hidden">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      {notice.content_type === 'image' ? (
                        <Image className="h-4 w-4 text-blue-500" />
                      ) : (
                        <Type className="h-4 w-4 text-green-500" />
                      )}
                      <h3 className="font-medium truncate">{notice.title}</h3>
                      {getStatusBadge(notice.status)}
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-gray-500 mb-2">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        <span>Start: {formatDateTime(notice.start_datetime)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        <span>End: {formatDateTime(notice.end_datetime)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span>Delay: {notice.show_delay_seconds}s</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        <span>Max: {notice.max_total_views} total, {notice.max_views_per_day}/day</span>
                      </div>
                      {notice.show_again_after_hours && (
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3 text-blue-500" />
                          <span className="text-blue-600 font-bangla">প্রতি {notice.show_again_after_hours} ঘণ্টা পর</span>
                        </div>
                      )}
                    </div>

                    {notice.content_type === 'text' && (
                      <p className="text-sm text-gray-600 line-clamp-2">{notice.content}</p>
                    )}
                    {notice.content_type === 'image' && (
                      <img 
                        src={notice.content} 
                        alt={notice.title}
                        className="h-20 w-auto rounded border object-cover"
                      />
                    )}
                  </div>

                  <div className="flex flex-col gap-1">
                    <Button
                      size="sm"
                      variant={notice.is_active ? "default" : "outline"}
                      onClick={() => handleTogglePublish(notice)}
                      className="w-24"
                    >
                      {notice.is_active ? (
                        <>
                          <EyeOff className="h-3 w-3 mr-1" />
                          Unpublish
                        </>
                      ) : (
                        <>
                          <Eye className="h-3 w-3 mr-1" />
                          Publish
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleViewStats(notice)}
                    >
                      <BarChart3 className="h-3 w-3 mr-1" />
                      Stats
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditModal(notice)}
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDeleteNotice(notice)}
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Notice Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Notice</DialogTitle>
            <DialogDescription>
              Create a notice to display to students after login
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateNotice} className="space-y-4">
            <div>
              <Label htmlFor="title">Notice Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter notice title"
                required
              />
            </div>

            <div>
              <Label>Content Type *</Label>
              <Select
                value={formData.content_type}
                onValueChange={(value) => {
                  setFormData({ ...formData, content_type: value, content: '' });
                  setImageFile(null);
                  setImagePreview(null);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">
                    <div className="flex items-center gap-2">
                      <Type className="h-4 w-4" />
                      Text Notice
                    </div>
                  </SelectItem>
                  <SelectItem value="image">
                    <div className="flex items-center gap-2">
                      <Image className="h-4 w-4" />
                      Image Notice
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.content_type === 'text' ? (
              <div>
                <Label htmlFor="content">Notice Content *</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Enter notice content..."
                  rows={4}
                  required
                />
              </div>
            ) : (
              <div>
                <Label>Upload Image * (Max 5MB)</Label>
                <div className="mt-2 border-2 border-dashed rounded-lg p-4 text-center">
                  {imagePreview ? (
                    <div className="relative">
                      <img src={imagePreview} alt="Preview" className="max-h-48 mx-auto rounded" />
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        className="absolute top-2 right-2"
                        onClick={() => { setImageFile(null); setImagePreview(null); }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-500">Click to upload image</p>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start_datetime">Start Date & Time *</Label>
                <Input
                  id="start_datetime"
                  type="datetime-local"
                  value={formData.start_datetime}
                  onChange={(e) => setFormData({ ...formData, start_datetime: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="end_datetime">End Date & Time *</Label>
                <Input
                  id="end_datetime"
                  type="datetime-local"
                  value={formData.end_datetime}
                  onChange={(e) => setFormData({ ...formData, end_datetime: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="show_delay">Popup Delay (seconds)</Label>
                <Input
                  id="show_delay"
                  type="number"
                  min="0"
                  max="60"
                  value={formData.show_delay_seconds}
                  onChange={(e) => setFormData({ ...formData, show_delay_seconds: parseInt(e.target.value) })}
                />
              </div>
              <div>
                <Label htmlFor="max_total">Total Views/Student</Label>
                <Input
                  id="max_total"
                  type="number"
                  min="1"
                  max="100"
                  value={formData.max_total_views}
                  onChange={(e) => setFormData({ ...formData, max_total_views: parseInt(e.target.value) })}
                />
              </div>
              <div>
                <Label htmlFor="max_day">Views/Day/Student</Label>
                <Input
                  id="max_day"
                  type="number"
                  min="1"
                  max="10"
                  value={formData.max_views_per_day}
                  onChange={(e) => setFormData({ ...formData, max_views_per_day: parseInt(e.target.value) })}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="show_again_hours" className="font-bangla">
                আবার দেখাও (ঘণ্টা পর) - Optional
              </Label>
              <Input
                id="show_again_hours"
                type="number"
                min="0"
                max="24"
                step="0.5"
                placeholder="খালি রাখলে Session-based"
                value={formData.show_again_after_hours}
                onChange={(e) => setFormData({ ...formData, show_again_after_hours: e.target.value })}
              />
              <p className="text-xs text-gray-500 mt-1 font-bangla">
                উদাহরণ: 2 দিলে প্রতি 2 ঘণ্টা পর পর দেখাবে (Login অবস্থায় থাকলেও)
              </p>
            </div>

            {/* Optional Link/Button Section */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <Label className="text-sm font-medium text-gray-700 mb-2 block">
                Optional Link Button (নোটিশে বাটন যোগ করুন)
              </Label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="link_url" className="text-xs text-gray-500">Button URL</Label>
                  <Input
                    id="link_url"
                    type="url"
                    placeholder="https://example.com"
                    value={formData.link_url}
                    onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="link_text" className="text-xs text-gray-500">Button Text</Label>
                  <Input
                    id="link_text"
                    placeholder="যেমন: আরো দেখুন"
                    value={formData.link_text}
                    onChange={(e) => setFormData({ ...formData, link_text: e.target.value })}
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                দুটি ফিল্ডই পূরণ করলে নোটিশে একটি Gradient বাটন দেখা যাবে
              </p>
            </div>

            <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
              <AlertCircle className="h-4 w-4 inline mr-2" />
              Notice will be saved as draft. Click &quot;Publish&quot; after creation to make it visible to students.
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Creating...' : 'Create Notice'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Notice Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Notice</DialogTitle>
            <DialogDescription>
              Update notice details
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleUpdateNotice} className="space-y-4">
            <div>
              <Label htmlFor="edit_title">Notice Title *</Label>
              <Input
                id="edit_title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>

            {formData.content_type === 'text' ? (
              <div>
                <Label htmlFor="edit_content">Notice Content *</Label>
                <Textarea
                  id="edit_content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  rows={4}
                  required
                />
              </div>
            ) : (
              <div>
                <Label>Current Image</Label>
                {imagePreview && (
                  <img src={imagePreview} alt="Notice" className="max-h-48 rounded border mt-2" />
                )}
                <p className="text-xs text-gray-500 mt-1">Image cannot be changed. Create a new notice to use a different image.</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_start">Start Date & Time *</Label>
                <Input
                  id="edit_start"
                  type="datetime-local"
                  value={formData.start_datetime}
                  onChange={(e) => setFormData({ ...formData, start_datetime: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit_end">End Date & Time *</Label>
                <Input
                  id="edit_end"
                  type="datetime-local"
                  value={formData.end_datetime}
                  onChange={(e) => setFormData({ ...formData, end_datetime: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="edit_delay">Popup Delay (sec)</Label>
                <Input
                  id="edit_delay"
                  type="number"
                  min="0"
                  max="60"
                  value={formData.show_delay_seconds}
                  onChange={(e) => setFormData({ ...formData, show_delay_seconds: parseInt(e.target.value) })}
                />
              </div>
              <div>
                <Label htmlFor="edit_total">Total Views</Label>
                <Input
                  id="edit_total"
                  type="number"
                  min="1"
                  max="100"
                  value={formData.max_total_views}
                  onChange={(e) => setFormData({ ...formData, max_total_views: parseInt(e.target.value) })}
                />
              </div>
              <div>
                <Label htmlFor="edit_day">Views/Day</Label>
                <Input
                  id="edit_day"
                  type="number"
                  min="1"
                  max="10"
                  value={formData.max_views_per_day}
                  onChange={(e) => setFormData({ ...formData, max_views_per_day: parseInt(e.target.value) })}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="edit_show_again" className="font-bangla">
                আবার দেখাও (ঘণ্টা পর) - Optional
              </Label>
              <Input
                id="edit_show_again"
                type="number"
                min="0"
                max="24"
                step="0.5"
                placeholder="খালি রাখলে Session-based"
                value={formData.show_again_after_hours}
                onChange={(e) => setFormData({ ...formData, show_again_after_hours: e.target.value })}
              />
              <p className="text-xs text-gray-500 mt-1 font-bangla">
                উদাহরণ: 2 দিলে প্রতি 2 ঘণ্টা পর পর দেখাবে
              </p>
            </div>

            {/* Optional Link/Button Section for Edit */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <Label className="text-sm font-medium text-gray-700 mb-2 block">
                Optional Link Button (নোটিশে বাটন যোগ করুন)
              </Label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit_link_url" className="text-xs text-gray-500">Button URL</Label>
                  <Input
                    id="edit_link_url"
                    type="url"
                    placeholder="https://example.com"
                    value={formData.link_url || ''}
                    onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="edit_link_text" className="text-xs text-gray-500">Button Text</Label>
                  <Input
                    id="edit_link_text"
                    placeholder="যেমন: আরো দেখুন"
                    value={formData.link_text || ''}
                    onChange={(e) => setFormData({ ...formData, link_text: e.target.value })}
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                দুটি ফিল্ডই পূরণ করলে নোটিশে একটি Gradient বাটন দেখা যাবে
              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stats Modal */}
      <Dialog open={showStatsModal} onOpenChange={setShowStatsModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Notice Statistics</DialogTitle>
            <DialogDescription>
              {selectedNotice?.title}
            </DialogDescription>
          </DialogHeader>

          {noticeStats && (
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardContent className="pt-6 text-center">
                  <Users className="h-8 w-8 mx-auto text-blue-500 mb-2" />
                  <p className="text-2xl font-bold">{noticeStats.unique_viewers}</p>
                  <p className="text-sm text-gray-500">Unique Students</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <Eye className="h-8 w-8 mx-auto text-green-500 mb-2" />
                  <p className="text-2xl font-bold">{noticeStats.total_views}</p>
                  <p className="text-sm text-gray-500">Total Views</p>
                </CardContent>
              </Card>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowStatsModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NoticeManagement;
